import styles from "./index.module.css";
import "./default.style.css";

import React from "react";
import DataComponent, {executeComponentCallback} from 'Core/components/DataComponent';
import PropTypes from "prop-types";
import {getArray, getBoolean, getNumber, getString, isset} from 'Core/helpers/data';
import ConditionListItem, {ConditionListFieldItemDataObject, ConditionListItemDataObject} from "./ListItem";
import FormWrapper from 'Core/components/advanced/FormWrapper';
import Button, {
	BUTTON_DISPLAY_TYPE,
	BUTTON_DISPLAY_TYPES,
	BUTTON_STYLE,
	BUTTON_STYLES
} from 'Core/components/display/Button';
import {
	CONDITION_LIST_FIELD_DISPLAY_TYPE,
	CONDITION_LIST_FIELD_DISPLAY_TYPES,
	CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE_COMPARATOR_MAP, 
	CONDITION_LIST_FIELD_ITEM_EMPTY_VALUES,
	CONDITION_LIST_VALUE_CLEAR_TYPE,
	CONDITION_LIST_VALUE_CLEAR_TYPES
} from './const';
import {
	forEach,
	get,
	set,
	cloneDeep,
	omit,
	isEqual,
	find,
	isPlainObject,
	map,
	difference,
	isFunction,
	reject,
	findIndex,
	isEmpty
} from 'lodash';
import {CSS_UNITS, LOGICAL_OPERATOR} from 'Core/const/global';
import {icon_font_close_symbol, icon_font_create_symbol, icon_font_delete_symbol} from 'Config/app';
import CheckboxInput from 'Core/components/input/CheckboxInput';
import {Tooltip} from "Core/components/global/Tooltip";
import {closeDialog, openDialog} from 'Core/helpers/dialog';
import ConfirmDialog from 'Core/components/dialogs/ConfirmDialog';
import {v4} from "uuid";
import ToggleInput from 'Core/components/input/ToggleInput';
import {blinkOpacitySlowAnimation} from 'Core/helpers/animation';
import Label from 'Core/components/display/Label';
import TooltipDropdown from 'Core/components/display/TooltipDropdown';

class ConditionList extends DataComponent {
	constructor(props) {
		super(props, {
			/**
			 * Condition items
			 * @type {ConditionListItemDataObject[]}
			 */
			data: [],

			/**
			 * List of selected condition items
			 * @type {ConditionListItemDataObject[]}
			 */
			selected: [],
		}, {
			translationPath: 'ConditionListComponent',
			domPrefix: 'condition-list-component',
			enableLoadOnDataPropChange: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: [
				// Ignore 'onChange' because anonymous functions (that are usually used for events) are generated on each 
				// parent component render, so they are always different and optimization will not work. This is true for 
				// any function prop.
				'onChange', 'onValidationErrorsChange'
			],
			// Include only state fields relevant for rendering the component
			// @note This component extends and abstract component that might have some state fields.
			optimizedUpdateIncludeState: ['data', 'errors', 'selected'],
			// Set to false because errors are loaded form props and cleared by item child component
			clearValidationErrorsOnChange: false,
		});

		// Selection methods
		this.selectItem = this.selectItem.bind(this);
		this.unselectItem = this.unselectItem.bind(this);
		this.toggleSelectItem = this.toggleSelectItem.bind(this);
		this.selectAllItems = this.selectAllItems.bind(this);
		this.unselectAllItems = this.unselectAllItems.bind(this);
		this.toggleAllItems = this.toggleAllItems.bind(this);
		this.unselectMissingItems = this.unselectMissingItems.bind(this);
		this.isItemSelected = this.isItemSelected.bind(this);
		this.areAllItemsSelected = this.areAllItemsSelected.bind(this);

		// Action methods
		this.removeSelectedItems = this.removeSelectedItems.bind(this);
		this.clearItems = this.clearItems.bind(this);

		// Render methods
		this.getStyle = this.getStyle.bind(this);
		this.getScrollParent = this.getScrollParent.bind(this);
		this.renderInsertDropdown = this.renderInsertDropdown.bind(this);
		this.renderItems = this.renderItems.bind(this);
	}
	
	componentDidUpdate(prevProps, prevState, snapshot) {
		return super.componentDidUpdate(prevProps, prevState, snapshot)
			// Update errors in local state based on 'errors' prop.
			.then(() => {
				if (!isEqual(prevProps.errors, this.props.errors)) return this.setValidationErrors('', this.props.errors);
				else return Promise.resolve(this.state);
			});
	}
	
	
	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Get data to load into local component's state
	 * @description Create and return data that can be loaded directly into local component's state based on the raw
	 * external data (usually sent through props). In some sense this is a method that maps external data into format
	 * that component can use in its local state. This method should return data in the same format as 'getData' method.
	 * @note This method will not mutate the passed data.
	 *
	 * @param {any} rawData - External data that will be used to create local component's state compatible data.
	 * @return {any|null} Local component's state compatible data or null if data could not be loaded.
	 */
	getDataToLoad(rawData) {
		const {
			logicalOperatorSelector, fieldSelector, fieldDisplayType, fieldItemLabelSelector, fieldItemValueSelector, 
			fieldItemDisplayTypeSelector, fieldItemSupportedComparatorsSelector, comparatorSelector, valueSelector,
			fieldDisplayOptions, idSelector, ordinalSelector, fieldItemDisplayOptionsSelector, openParenthesisSelector,
			closeParenthesisSelector, enableParenthesis, mapData, emptyValues, 
		} = this.props;
		
		if (!mapData) return (Array.isArray(rawData) ? getArray(super.getDataToLoad(rawData)) : []);
		
		let result = [];
		
		// Go through each condition item and prepare it for use by this component
		forEach(getArray(super.getDataToLoad(rawData)), (i, index) => {
			// Handle condition field
			const rawField = get(i, fieldSelector);
			let field;
			switch (fieldDisplayType) {
				case CONDITION_LIST_FIELD_DISPLAY_TYPE.TEXT:
					field = new ConditionListFieldItemDataObject(rawField, rawField);
					break;

				case CONDITION_LIST_FIELD_DISPLAY_TYPE.ASYNC_SELECT:
				case CONDITION_LIST_FIELD_DISPLAY_TYPE.CUSTOM:
					field = new ConditionListFieldItemDataObject(
						getString(rawField, fieldItemLabelSelector),
						get(rawField, fieldItemValueSelector),
						cloneDeep(omit(rawField, [
							fieldItemLabelSelector,
							fieldItemValueSelector,
							fieldItemDisplayTypeSelector,
							fieldItemDisplayOptionsSelector,
							fieldItemSupportedComparatorsSelector
						])),
						get(rawField, fieldItemDisplayTypeSelector),
						get(rawField, fieldItemDisplayOptionsSelector),
						get(rawField, fieldItemSupportedComparatorsSelector)
					);
					break;
					
				case CONDITION_LIST_FIELD_DISPLAY_TYPE.SELECT:
					if (isPlainObject(rawField)) {
						field = new ConditionListFieldItemDataObject(
							getString(rawField, fieldItemLabelSelector),
							get(rawField, fieldItemValueSelector),
							cloneDeep(omit(rawField, [
								fieldItemLabelSelector, 
								fieldItemValueSelector, 
								fieldItemDisplayTypeSelector,
								fieldItemDisplayOptionsSelector,
								fieldItemSupportedComparatorsSelector,
								openParenthesisSelector,
								closeParenthesisSelector
							])),
							get(rawField, fieldItemDisplayTypeSelector),
							get(rawField, fieldItemDisplayOptionsSelector),
							get(rawField, fieldItemSupportedComparatorsSelector)
						);
					} else if (isset(rawField) && rawField !== null) {
						const options = getArray(fieldDisplayOptions, 'options');
						const fieldItem = find(options, {[fieldItemValueSelector]: rawField});
						field = new ConditionListFieldItemDataObject(
							getString(fieldItem, fieldItemLabelSelector),
							get(fieldItem, fieldItemValueSelector),
							cloneDeep(omit(fieldItem, [
								fieldItemLabelSelector,
								fieldItemValueSelector,
								fieldItemDisplayTypeSelector,
								fieldItemDisplayOptionsSelector,
								fieldItemSupportedComparatorsSelector,
								openParenthesisSelector,
								closeParenthesisSelector
							])),
							get(fieldItem, fieldItemDisplayTypeSelector),
							get(fieldItem, fieldItemDisplayOptionsSelector),
							get(fieldItem, fieldItemSupportedComparatorsSelector)
						);
					} else {
						field = new ConditionListFieldItemDataObject();
					}
					break;
					
				default:
					field = new ConditionListFieldItemDataObject();
			}
			
			// Handle condition item
			result.push(new ConditionListItemDataObject(
				get(i, logicalOperatorSelector),
				field,
				getString(i, comparatorSelector),
				get(i, valueSelector, emptyValues[field.displayType]),
				enableParenthesis ? getNumber(i, openParenthesisSelector) : 0,
				enableParenthesis ? getNumber(i, closeParenthesisSelector) : 0,
				get(i, idSelector),
				(index + 1), // +1 so it starts from 1 not 0
				cloneDeep(omit(i, [
					logicalOperatorSelector,
					fieldSelector,
					comparatorSelector,
					valueSelector,
					idSelector,
					ordinalSelector,
					openParenthesisSelector,
					closeParenthesisSelector,
					'GUIID'
				])),
				getString(i, 'GUIID', v4(), true)
			));
		});
		
		return result;
	}

	/**
	 * Get data that will be returned to the parent component
	 * @description Create and return data that will be returned to paren component (usually through onChange event)
	 * based on the local component's raw state data
	 * @note This method will not mutate the passed data.
	 *
	 * @return {Object[]} Data that will be returned to the parent component.
	 */
	getDataToReturn(data) {
		const {
			logicalOperatorSelector, fieldSelector, fieldItemLabelSelector, fieldItemValueSelector,
			fieldItemDisplayTypeSelector, fieldItemSupportedComparatorsSelector, comparatorSelector, valueSelector, 
			idSelector, ordinalSelector, fieldDisplayType, fieldSimpleValue, fieldItemDisplayOptionsSelector,
			openParenthesisSelector, closeParenthesisSelector, enableParenthesis, mapData, emptyValues, 
		} = this.props;
		
		/** @type {ConditionListItemDataObject[]} */
		const dataToProcess = getArray(cloneDeep(data ? data : this.state.data));
		
		if (!mapData) return (Array.isArray(dataToProcess) ? cloneDeep(dataToProcess) : []);

		return dataToProcess.map((i, index) => {
			// Handle condition field
			let field;
			const fieldToProcess = cloneDeep(i.field);
			if (fieldSimpleValue) {
				field = fieldToProcess.value;
			} else {
				switch (fieldDisplayType) {
					case CONDITION_LIST_FIELD_DISPLAY_TYPE.TEXT:
						field = fieldToProcess.value;
						break;
						
					case CONDITION_LIST_FIELD_DISPLAY_TYPE.SELECT:
					case CONDITION_LIST_FIELD_DISPLAY_TYPE.ASYNC_SELECT:
					case CONDITION_LIST_FIELD_DISPLAY_TYPE.CUSTOM:
						field = {
							[fieldItemLabelSelector]: fieldToProcess.label,
							[fieldItemValueSelector]: fieldToProcess.value,
							[fieldItemDisplayTypeSelector]: fieldToProcess.displayType,
							[fieldItemDisplayOptionsSelector]: fieldToProcess.displayOptions,
							[fieldItemSupportedComparatorsSelector]: fieldToProcess.supportedComparators,
							...get(fieldToProcess, 'data', {})
						};
						break;
						
					default:
						field = null;
				}
			}
			
			let result = {
				[idSelector]: i.id ? i.id : null,
				[logicalOperatorSelector]: i.logicalOperator ? i.logicalOperator : null,
				[fieldSelector]: field ? field : null,
				[comparatorSelector]: i.comparator ? i.comparator : null,
				[valueSelector]: isset(i.value) ? i.value : emptyValues[i.field.displayType],
				[ordinalSelector]: (index + 1), // +1 so it starts from 1 not 0
				...get(i, 'data', {}),
				GUIID: i.GUIID
			};
			if (enableParenthesis) {
				result[openParenthesisSelector] = getNumber(i, 'openParenthesis');
				result[closeParenthesisSelector] = getNumber(i, 'closeParenthesis');
			}
			return result;
		});
	}

	/**
	 * Load data into local component's state and call component's update method if data is different from current data
	 * @note This method will not mutate the passed data. We recommend that you override this method in your child
	 * classes if you need to alter the loading procedure. We do not recommend overriding the 'load' method!
	 *
	 * @param {any} data - Data to load into local component's state. If data is the same as current local component's
	 * state data it will not be loaded and component's update method will not be called.
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been loaded.
	 */
	defaultLoad(data) {
		// Get current and data to load
		const currentData = this.getData();
		const dataToLoad = this.getDataToLoad(data);

		// Get current and data to load that can be compared
		// @note GUI ID is auto-generated in 'getDataToLoad' method, so it should not be compared.
		const currentDataToCompare = getArray(currentData).map(i => omit(i, ['GUIID']));
		const dataToLoadToCompare = getArray(dataToLoad).map(i => omit(i, ['GUIID']));

		// Load data if it is different than current data
		if(!isEqual(currentDataToCompare, dataToLoadToCompare)) {
			return this.setState({data: dataToLoad})
				.then(this.unselectMissingItems);
		}
		// Do not load data if it is not different from current data
		return new Promise(resolve => resolve(this.state));
	}
	
	/**
	 * Get component's main data from local state
	 * @return {ConditionListItemDataObject[]} Cloned component's main data from local state.
	 */
	getData() { return getArray(super.getData()); }


	// Selection methods ------------------------------------------------------------------------------------------------
	/**
	 * Select condition list item
	 * @param {string|Object} predicate - Selector used to find the item in the list. If predicate is a string it will be
	 * treated as a GUIID. If predicate is an object it will be used as a standard predicate (for ex.: { id: '123' }).
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	selectItem(predicate) {
		const items = this.getData();
		
		let item;
		if (typeof predicate === 'string') item = find(items, {GUIID: predicate});
		else if (isPlainObject(predicate)) item = find(items, predicate);

		return (item ? this.addStateArrayItem('selected', item) : Promise.resolve(this.state));
	}

	/**
	 * Unselect condition list item
	 * @param {string|Object} predicate - Selector used to find the item in the list. If predicate is a string it will be
	 * treated as a GUIID. If predicate is an object it will be used as a standard predicate (for ex.: { id: '123' }).
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	unselectItem(predicate) {
		if (typeof predicate === 'string') return this.removeStateArrayItem('selected', {GUIID: predicate});
		else if (isPlainObject(predicate)) return this.removeStateArrayItem('selected', predicate);
	}

	/**
	 * Toggle select for condition list item
	 * @param {string|Object} predicate - Selector used to find the item in the list. If predicate is a string it will be
	 * treated as a GUIID. If predicate is an object it will be used as a standard predicate (for ex.: { id: '123' }).
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	toggleSelectItem(predicate) {
		let predicateObj = null;
		if (typeof predicate === 'string') predicateObj = {GUIID: predicate};
		else if (isPlainObject(predicate)) predicateObj = predicate;

		if (predicateObj) {
			const items = getArray(this.getData());
			const selectedItems = getArray(this.state, 'selected');
			const item = find(items, predicateObj);
			const selectedItem = find(selectedItems, predicateObj);
			if (item) {
				if (selectedItem) return this.removeStateArrayItem('selected', predicateObj);
				else return this.addStateArrayItem('selected', item);
			}
		}

		return Promise.resolve(this.state);
	}

	/**
	 * Select all condition list items
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	selectAllItems() { return this.setState({selected: getArray(this.getData())}); }

	/**
	 * Clear condition list items selection
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	unselectAllItems() { return this.setState({selected: []}); }

	/**
	 * Toggle condition list items selection
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	toggleAllItems() {
		const allSelected = this.areAllItemsSelected();
		return (allSelected ? this.unselectAllItems() : this.selectAllItems());
	}

	/**
	 * Unselect condition pairs that are not in the list
	 * @note Check is performed using items GUI ID ('GUIID').
	 * @return {Promise<object>} Promise that is resolved with entire component's local state after it has been updated.
	 */
	unselectMissingItems() {
		return this.setState({
			selected: getArray(this.state, 'selected')
				.filter(item => map(getArray(this.getData()), 'GUIID').includes(item.GUIID))
		})
	}

	/**
	 * Check if condition item is selected
	 *
	 * @param {ConditionListItemDataObject} item - Item object.
	 * @return {boolean}
	 */
	isItemSelected(item) {
		const selectedItems = getArray(this.state, 'selected');
		return !!find(selectedItems, {GUIID: item.GUIID});
	}

	/**
	 * Check if all condition items are selected
	 * @return {boolean}
	 */
	areAllItemsSelected() {
		const items = getArray(this.getData());
		const selectedItems = getArray(this.state, 'selected');
		return (!!items.length && !difference(map(items, 'GUIID'), map(selectedItems, 'GUIID')).length);
	}


	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Remove selected items
	 */
	removeSelectedItems() {
		if (getBoolean(this.props, 'confirmDeleteSelected')) {
			openDialog(
				'',
				ConfirmDialog,
				{
					message: this.t('remove_items_confirm_message'),
					onYes: GUIID => {
						const selectedItemGUIIDs = map(getArray(this.state, 'selected'), 'GUIID');
						return this.setData(reject(getArray(this.getData()), i => selectedItemGUIIDs.includes(i.GUIID)))
							.then(() => this.unselectAllItems())
							.then(() => this.update())
							.then(() => closeDialog(GUIID));
					},
				}, {
					id: 'confirm-remove-selected',
					closeOnEscape: true,
					closeOnClickOutside: true,
					hideCloseBtn: true,
					maxWidth: 550,
				}
			);
		} else {
			const selectedItemGUIIDs = map(getArray(this.state, 'selected'), 'GUIID');
			return this.setData(reject(getArray(this.getData()), i => selectedItemGUIIDs.includes(i.GUIID)))
				.then(() => this.unselectAllItems())
				.then(() => this.update());
		}
	};

	/**
	 * Remove all items
	 */
	clearItems() {
		if (getBoolean(this.props, 'confirmClear')) {
			openDialog(
				'',
				ConfirmDialog,
				{
					message: this.t('clear_item_confirm_message'),
					onYes: GUIID => {
						this.clearData()
							.then(() => this.setState({selected: []}))
							.then(() => this.update())
							.then(() => closeDialog(GUIID));
					},
				}, {
					id: 'confirm-remove-all',
					closeOnEscape: true,
					closeOnClickOutside: true,
					hideCloseBtn: true,
					maxWidth: 550,
				}
			);
		} else {
			this.clearData()
				.then(() => this.setState({selected: []}))
				.then(() => this.update());
		}
	}

	/**
	 * Remove item from component's main data in local state
	 * @note This method is used if component's main data is an array of objects.
	 *
	 * @param {Object} predicate - Selector object used to find the item (for example: { id: '123' }).
	 * @param {string, Array<string, 'asc'|'desc'>|function|null} [sort=null] - Sort the items after update. String will
	 * sort items by the field name in the ascending order. Array expects two items: field name to sort by and sort
	 * order. Function should define a custom sort method.
	 * @return {Promise<any>} Promise that is resolved with entire component's local state after data item has been
	 * removed.
	 */
	removeItem(predicate, sort = null) {
		if (getBoolean(this.props, 'confirmDelete')) {
			openDialog(
				'',
				ConfirmDialog,
				{
					message: this.t('remove_item_confirm_message'),
					onYes: GUIID => {
						const items = this.getData();
						const item = find(items, predicate);
						const itemIndex = findIndex(items, predicate);
						const itemOpenParenthesis = getNumber(item, 'openParenthesis');
						const itemCloseParenthesis = getNumber(item, 'closeParenthesis');
						
						const nextItem = get(items, `[${itemIndex + 1}]`);
						const nextItemOpenParenthesis = getNumber(nextItem, 'openParenthesis');

						const prevItem = get(items, `[${itemIndex - 1}]`);
						const prevItemCloseParenthesis = getNumber(prevItem, 'closeParenthesis');
						
						super.removeItem(predicate)
							.then(async () => {
								// Handle open parenthesis
								if (isset(nextItem)) {
									const itemCarryParenthesis = (itemOpenParenthesis - itemCloseParenthesis);
									await this.setItemValue(
										nextItem, 
										'openParenthesis',
										(nextItemOpenParenthesis + itemCarryParenthesis)
									);
								}
								// Handle close parenthesis
								if (isset(prevItem)) {
									const itemCarryParenthesis = (itemCloseParenthesis - itemOpenParenthesis);
									await this.setItemValue(
										prevItem,
										'closeParenthesis',
										(prevItemCloseParenthesis + (itemCarryParenthesis > 0 ? itemCarryParenthesis : 0))
									);
								}
							})
							.then(() => this.update())
							.then(() => closeDialog(GUIID));
					},
				}, {
					id: 'confirm-remove-item',
					closeOnEscape: true,
					closeOnClickOutside: true,
					hideCloseBtn: true,
					maxWidth: 550,
				}
			);
		} else {
			super.removeItem(predicate)
				.then(() => this.update());
		}
	}
	

	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Get style object for component wrapper element
	 */
	getStyle() {
		const {maxHeight} = this.props;
		let result = {};

		// Get max height
		if (typeof maxHeight === 'string') {
			if (CSS_UNITS.some(i => maxHeight.endsWith(i))) set(result, 'maxHeight', maxHeight);
			else {
				const maxHeightNumber = parseFloat(maxHeight);
				if (!isNaN(maxHeightNumber) && maxHeightNumber > 0) {
					set(result, 'maxHeight', `${maxHeightNumber}px`);
				}
			}
		} else if (typeof maxHeight === 'number' && maxHeight > 0) {
			set(result, 'maxHeight', `${maxHeight}px`);
		}

		return result;
	}

	/**
	 * Get the scroll parent of the list
	 * @description Returns a scroll parent specified by 'scrollParentSelector' prop if max height is not set. Otherwise,
	 * returns items wrapper.
	 * @return {any}
	 */
	getScrollParent() {
		const {scrollParentSelector} = this.props;
		const hasMaxHeight = get(this.getStyle(), 'maxHeight');
		return document.querySelector(
			!hasMaxHeight && !!scrollParentSelector ? scrollParentSelector : `.${styles['itemsWrapper']}`
		);
	}
	
	/**
	 * Render insert item dropdown
	 * 
	 * @param {number} index - Index to insert the item at.
	 * @param {Object} [newItemUpdater={}] - New insert item updater.
	 * @param {function} [onBeforeInsert] - Event function called before item is inserted.
	 * @param {function} [onAfterInsert] - Event function called after item is inserted with the newly inserted item.
	 * @return {JSX.Element}
	 */
	renderInsertDropdown(index, newItemUpdater = {}, onBeforeInsert, onAfterInsert) {
		const {disabled} = this.props;
		
		return (
			<TooltipDropdown
				element="div"
				label={
					<Button
						icon={icon_font_create_symbol}
						displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
						displayStyle={BUTTON_STYLE.SUBTLE}
						disabled={disabled}
					/>
				}
				position="bottom-start"
				duration={0}
				distance={0}
				disabled={disabled}
			>
				<div
					className="dropdown-item"
					onClick={async () => {
						if (typeof onBeforeInsert === 'function') await onBeforeInsert();
						
						let newItem = new ConditionListItemDataObject();
						newItem = {...newItem, ...newItemUpdater};
						
						return this.insertItem(newItem, index)
							.then(item => {
								blinkOpacitySlowAnimation(
									document.querySelector(`.condition-list-item-component[data-guiid='${item.GUIID}']`),
									350
								);
								
								if (typeof onAfterInsert === 'function') onAfterInsert(item);
							})
							.then(() => this.update());
					}}
				>
					<Label icon="plus" content={this.t('insert_item')} supportHtml={true}/>
				</div>
				<div
					className="dropdown-item"
					onClick={async () => {
						if (typeof onBeforeInsert === 'function') await onBeforeInsert();

						let newItem = new ConditionListItemDataObject(undefined, undefined, undefined, undefined, 1, 1);
						newItem = {
							...newItem, 
							...omit(newItemUpdater, ['openParenthesis', 'closeParenthesis']),
							openParenthesis: getNumber(newItemUpdater, 'openParenthesis') + 1,
							closeParenthesis: getNumber(newItemUpdater, 'closeParenthesis') + 1,
						};
						
						return this.insertItem(newItem, index)
							.then(item => {
								blinkOpacitySlowAnimation(
									document.querySelector(`.condition-list-item-component[data-guiid='${item.GUIID}']`),
									350
								);

								if (typeof onAfterInsert === 'function') onAfterInsert(item);
							})
							.then(() => this.update());
					}}
				>
					<Label icon="plus" content={this.t('insert_group')} supportHtml={true}/>
				</div>
			</TooltipDropdown>
		);
	}
	
	/**
	 * Render items
	 * @param {ConditionListItemDataObject[]} items - Items to render.
	 * @return {JSX.Element|JSX.Element[]}
	 */
	renderItems(items) {
		const {
			itemStyleName, selectable, fieldDisplayType, fieldDisplayOptions, fieldCustomComponent, 
			comparatorDisplayTypeMap, fieldItemLabelSelector, fieldItemValueSelector, fieldItemDisplayTypeSelector, 
			fieldItemSupportedComparatorsSelector, itemClassName, fieldItemDisplayOptionsSelector, enableParenthesis, 
			clearValueType, showItemActions, disabled, readOnly, 
		} = this.props;

		/**
		 * Get the number of opened parentheses for the item at the specified index from the list
		 * 
		 * @param {number} index - Items index in the list. 
		 * @return {number}
		 */
		const getParenthesisLevel = index => {
			if (index === 0) return getNumber(items, `[${index}].openParenthesis`);
			return items.slice(0, index + 1)
				.reduce((prev, curr) => prev + (getNumber(curr, 'openParenthesis') - getNumber(curr, 'closeParenthesis')),0) 
				+ getNumber(items, `[${index}].closeParenthesis`);
		};
		/**
		 * Get CSS indent value for a specified list item
		 * @param {number} index - Items index in the list.
		 * @return {string} CSS string that can be used to indent the item using left margin.
		 */
		const getItemIndent = index => `calc(var(--condition-list-parenthesis-indent) * ${getParenthesisLevel(index)})`;
		/**
		 * Get CSS indent value for a specified parenthesis
		 * @param {number} itemIndex - Items index in the list.
		 * @param {number} index - Parenthesis index used if there are multiple consequential parenthesis.
		 * @return {string} CSS string that can be used to indent the parenthesis using left margin.
		 */
		const getParenthesisIndent = (itemIndex, index) => 
			`calc(var(--condition-list-parenthesis-indent) * ${getParenthesisLevel(itemIndex) - index - 1})`;
		
		if (!items || items.length === 0) {
			return (
				!readOnly ?
					<div className="item-wrapper">
						<div className="insert-between-parenthesis insert-parenthesis first">
							{enableParenthesis ? 
								this.renderInsertDropdown(0) :
								<Button
									icon={icon_font_create_symbol}
									displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
									displayStyle={BUTTON_STYLE.SUBTLE}
									onClick={() => this.insertItem(new ConditionListItemDataObject(), 0)
										.then(item => blinkOpacitySlowAnimation(
											document.querySelector(`.condition-list-item-component[data-guiid='${item.GUIID}']`),
											350
										))
										.then(() => this.update())
									}
								/>
							}
						</div>
					</div>
					: null
			);
		}

		return (
			items.map((item, itemIndex) =>
				<div key={item.GUIID} className={`item-wrapper`}>
					{
						!readOnly && (
						(getNumber(item, 'openParenthesis') > 0 && getNumber(items[itemIndex - 1], 'closeParenthesis') > 0) ||
						(getNumber(item, 'openParenthesis') > 0 && itemIndex === 0)) ?
						<div 
							className={
								`insert-between-parenthesis insert-parenthesis ` +
								(getNumber(item, 'openParenthesis') > 0 && itemIndex === 0 ? 'first' : '')
							}
						>
							{this.renderInsertDropdown(itemIndex)}
						</div>
						: null
					}

					{itemIndex > 0 ?
						<div className={`logical-operator-wrapper`}>
							<ToggleInput
								icons={{
									checked: this.t('OR', 'constants'),
									unchecked: this.t('AND', 'constants')
								}}
								checked={(getString(item, 'logicalOperator', null) === LOGICAL_OPERATOR.OR)}
								disabled={disabled}
								readOnly={readOnly}
								onChange={e => this.handleItemValueChangeAndUpdate(
									{GUIID: item.GUIID},
									'logicalOperator',
									(getBoolean(e, 'target.checked') ? LOGICAL_OPERATOR.OR : LOGICAL_OPERATOR.AND)
								)}
							/>
						</div>
						: null
					}
					
					{getNumber(item, 'openParenthesis') > 0 ?
						Array.from(Array(getNumber(item, 'openParenthesis')).keys()).reverse().map((n, pIdx) =>
							<div key={v4()} className={`parenthesis`}>
								<div
									className={'item-parenthesis item-open-parenthesis'}
									style={{marginLeft: getParenthesisIndent(itemIndex, n)}}
								><span>(</span></div>
								
								{
									!readOnly && 
									getNumber(item, 'openParenthesis') > 1 && pIdx < getNumber(item, 'openParenthesis') - 1 ?
										<div className={`insert-open-parenthesis insert-parenthesis`}>
											{this.renderInsertDropdown(itemIndex, {openParenthesis: pIdx + 1}, () => {
												const itemOpenParenthesis = getNumber(item, 'openParenthesis');
												return this.setItemValue(
													item, 'openParenthesis', (itemOpenParenthesis - (pIdx + 1))
												);
											})}
										</div>
									: null
								}
							</div>
						) : null
					}

					<div className={`condition-list-item-wrapper`} style={{marginLeft: getItemIndent(itemIndex)}}>
						<ConditionListItem
							key={item.GUIID}
							styleName={itemStyleName}
							className={itemClassName}
							readOnly={readOnly}
							selectable={selectable}
							selected={this.isItemSelected(item)}
							fieldDisplayType={fieldDisplayType}
							fieldDisplayOptions={fieldDisplayOptions}
							fieldCustomComponent={fieldCustomComponent}
							fieldItemLabelSelector={fieldItemLabelSelector}
							fieldItemValueSelector={fieldItemValueSelector}
							fieldItemDisplayTypeSelector={fieldItemDisplayTypeSelector}
							fieldItemDisplayOptionsSelector={fieldItemDisplayOptionsSelector}
							fieldItemSupportedComparatorsSelector={fieldItemSupportedComparatorsSelector}
							data={item}
							index={itemIndex}
							isLast={itemIndex === items.length - 1}
							comparatorDisplayTypeMap={comparatorDisplayTypeMap}
							enableParenthesis={enableParenthesis}
							clearValueType={clearValueType}
							showActions={showItemActions}
							disabled={disabled}
							onSelect={this.toggleSelectItem}
							onDelete={GUIID => this.removeItem({GUIID})}
							onChange={v => this.handleItemChangeAndUpdate({GUIID: item.GUIID}, v)}
							onInsertItem={(ordinal, position) => {
								if (enableParenthesis) {
									const openParenthesis = getNumber(item, 'openParenthesis');
									const closeParenthesis = getNumber(item, 'closeParenthesis');

									let newItem = new ConditionListItemDataObject();
									// When inserting above an element that has open parenthesis, move them to the new item
									if (position === 'above' && openParenthesis > 0) newItem.openParenthesis = openParenthesis;
									// When inserting below an element that has close parenthesis, move them to the new item
									if (position === 'below' && closeParenthesis > 0) newItem.closeParenthesis = closeParenthesis;

									return this.insertItem(newItem, ordinal)
										.then(async res => {
											// When inserting above an element that has open parenthesis, remove them from the item
											if (position === 'above' && openParenthesis > 0) {
												await this.setItemValue(item, 'openParenthesis', 0)
											}
											// When inserting below an element that has close parenthesis, remove them from the item
											if (position === 'below' && closeParenthesis > 0) {
												await this.setItemValue(item, 'closeParenthesis', 0)
											}
											return res;
										})
										.then(item => blinkOpacitySlowAnimation(
											document.querySelector(`.condition-list-item-component[data-guiid='${item.GUIID}']`),
											350,
										))
										.then(() => this.update())
								} else {
									return this.insertItem(new ConditionListItemDataObject(), ordinal)
										.then(item => blinkOpacitySlowAnimation(
											document.querySelector(`.condition-list-item-component[data-guiid='${item.GUIID}']`),
											350,
										))
										.then(() => this.update())	
								}
							}}
							onInsertGroup={(ordinal, position) => {
								const openParenthesis = getNumber(item, 'openParenthesis');
								const closeParenthesis = getNumber(item, 'closeParenthesis');

								let newItem = new ConditionListItemDataObject(
									undefined, undefined, undefined, undefined, 1, 1
								);
								// When inserting above an element that has open parenthesis, move them to the new item
								if (position === 'above' && openParenthesis > 0) {
									newItem.openParenthesis += openParenthesis;
								}
								// When inserting below an element that has close parenthesis, move them to the new item
								if (position === 'below' && closeParenthesis > 0) {
									newItem.closeParenthesis += closeParenthesis;
								}
								
								this.insertItem(newItem, ordinal)
									.then(async res => {
										// When inserting above an element that has open parenthesis, remove them from the item
										if (position === 'above' && openParenthesis > 0) {
											await this.setItemValue(item, 'openParenthesis', 0)
										}
										// When inserting below an element that has close parenthesis, remove them from the item
										if (position === 'below' && closeParenthesis > 0) {
											await this.setItemValue(item, 'closeParenthesis', 0)
										}
										return res;
									})
									.then(item => blinkOpacitySlowAnimation(
										document.querySelector(`.condition-list-item-component[data-guiid='${item.GUIID}']`),
										350,
									))
									.then(() => this.update())
							}}
							errors={this.getValidationErrors(item.GUIID)}
							onValidationErrorsChange={(GUIID, errors) => executeComponentCallback(
								this.props.onValidationErrorsChange,
								(isEmpty(errors) ? omit(this.state.errors, GUIID) : {...this.state.errors, [GUIID]: errors})
							)}
						/>
					</div>

					{getNumber(item, 'closeParenthesis') > 0 ?
						Array(getNumber(item, 'closeParenthesis')).fill(null).map((i, pIdx) =>
							<div key={v4()} className={`parenthesis`}>
								<div key={v4()}
									className={'item-parenthesis item-close-parenthesis'}
									style={{marginLeft: getParenthesisIndent(itemIndex, pIdx)}}
								><span>)</span></div>

								{
									!readOnly && 
									getNumber(item, 'closeParenthesis') > 1 && pIdx < getNumber(item, 'closeParenthesis') - 1 ?
									<div className={`insert-close-parenthesis insert-parenthesis`}>
										{this.renderInsertDropdown(itemIndex + 1, {
											closeParenthesis: getNumber(item, 'closeParenthesis') - (pIdx + 1)
										}, () => {
											const itemCloseParenthesis = getNumber(item, 'closeParenthesis');
											return this.setItemValue(
												item, 'closeParenthesis', itemCloseParenthesis - (itemCloseParenthesis - (pIdx + 1))
											);
										})}
									</div>
									: null
								}
							</div>
						) : null
					}

					{!readOnly && getNumber(item, 'closeParenthesis') > 0 && itemIndex === items.length - 1 ?
						<div className={`insert-between-parenthesis insert-parenthesis last`}>
							{this.renderInsertDropdown(itemIndex + 1)}
						</div>
						: null
					}
				</div>
			)
		);
	}

	render() {
		const {
			styleName, toolbarStyleName, className, selectable, showLabels, showToolbar, customToolbarButtons, fieldLabel,
			comparatorLabel, valueLabel, showBottomToolbar, enableParenthesis, disabled, readOnly, 
		} = this.props;
		const items = this.getData();
		const hasMaxHeight = get(this.getStyle(), 'maxHeight');

		return (
			<div
				id={this.getDomId()}
				className={
					`${this.getOption('domPrefix')} ${styleName}-style ${className} ${styles['wrapper']} ` +
					`${selectable ? 'selectable' : ''} ${hasMaxHeight ? 'has-max-height' : ''} ` +
					`${disabled ? 'disabled' : ''} ${readOnly ? 'read-only' : ''} `
				}
				style={this.getStyle()}
			>
				{
					!readOnly && showToolbar ?
						<div
							className={
								`toolbar ${toolbarStyleName} ${styles['toolbar']} ${hasMaxHeight ? 'has-max-height' : ''}`
							}
						>
							<Tooltip
								tag="span"
								title={`${this.t('Add', 'general')}...`}
								size="small"
								position="top-center"
								arrow={true}
								interactive={false}
							>
							{enableParenthesis ?
								this.renderInsertDropdown(!!items ? items.length : 0, undefined, undefined, () => {
									const scrollParent = this.getScrollParent();
									if (!!scrollParent) scrollParent.scrollTo(0, scrollParent.scrollHeight);
								})
								:
								<Button
									icon={icon_font_create_symbol}
									displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
									displayStyle={BUTTON_STYLE.SUBTLE}
									disabled={disabled}
									onClick={() => this.insertItem(new ConditionListItemDataObject(), !!items ? items.length : 0)
										.then(item => {
											blinkOpacitySlowAnimation(
												document.querySelector(
													`.condition-list-item-component[data-guiid='${item.GUIID}']`
												),
												350
											);
											const scrollParent = this.getScrollParent();
											if (!!scrollParent) scrollParent.scrollTo(0, scrollParent.scrollHeight);
										})
										.then(() => this.update())
									}
								/>
							}
							</Tooltip>
							{selectable ?
								<>
									<div className="separator"/>
									<Tooltip
										tag="span"
										title={`${this.t('Remove selected', 'general')}...`}
										size="small"
										position="top-center"
										arrow={true}
										interactive={false}
									>
										<Button
											displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
											icon={icon_font_delete_symbol}
											onClick={this.removeSelectedItems}
											disabled={disabled || !getArray(this.state, 'selected').length}
										/>
									</Tooltip>
								</>
								: null
							}
							<div className="separator"/>
							<Tooltip
								tag="span"
								title={`${this.t('Remove all', 'general')}...`}
								size="small"
								position="top-center"
								arrow={true}
								interactive={false}
							>
								<Button
									displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
									icon={icon_font_close_symbol}
									onClick={this.clearItems}
									disabled={disabled || !getArray(this.getData()).length}
								/>
							</Tooltip>
							<div className="separator" />
							{getArray(customToolbarButtons).map((btn, idx) =>
								<Button
									key={idx}
									displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
									{...btn}
									onClick={e => {
										if (isFunction(btn.onClick)) {
											Promise.resolve(
												btn.onClick(e, this.getDataToReturn(getArray(this.state, 'selected')), items)
											).then(() => this.update());
										}
									}}
								/>
							)}
							{getArray(customToolbarButtons).length ? <div className="separator" /> : null}
						</div>
						: null
				}

				{
					showLabels ?
						<div className={`labels ${styles['labels']} ${hasMaxHeight ? 'has-max-height' : ''}`}>
							{
								!readOnly && selectable ?
									<div className={`select-wrapper`}>
										<CheckboxInput
											checked={this.areAllItemsSelected()}
											size={18}
											disabled={disabled}
											onChange={this.toggleAllItems}
										/>
									</div>
									: null
							}
							<div className={`field-label`}>{fieldLabel}</div>
							<div className={`comparator-label`}>{comparatorLabel}</div>
							<div className={`value-label`}>{valueLabel}</div>
						</div>
						: null
				}
				
				<FormWrapper className={`${styles['itemsWrapper']} ${hasMaxHeight ? styles['maxHeight'] : ''}`}>
					{this.renderItems(items)}
				</FormWrapper>

				{
					!readOnly && showBottomToolbar ?
						<div className={`bottom-toolbar ${styles['bottomToolbar']}`}>
							<Button
								displayType={BUTTON_DISPLAY_TYPE.NONE}
								icon={icon_font_create_symbol}
								label={this.t('Add', 'general')}
								onClick={() => this.addItem(new ConditionListItemDataObject()).then(() => this.update())}
							/>
						</div>
						: null
				}
			</div>
		);
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
ConditionList.propTypes = {
	...DataComponent.propTypes,
	
	// Component style name
	// @description Component style name is a name of the style that will be used to determine the CSS used to style the
	// component.
	styleName: PropTypes.string,
	// Condition item component style name
	// @description Condition item component style name is a name of the style that will be used to determine the CSS 
	// used to style the condition item component.
	itemStyleName: PropTypes.string,
	// Component toolbar style name
	toolbarStyleName: PropTypes.string,
	// Component's wrapper element id attribute
	id: PropTypes.string,
	// Component's wrapper element class attribute
	className: PropTypes.string,
	// Child item component's wrapper element class attribute
	itemClassName: PropTypes.string,
	// Max. height of the component
	maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	// Flag that determines whether the component is read-only
	readOnly: PropTypes.bool,
	// Flag that determines if condition items will be selectable
	// @note Selectable items can be removed or other actions can be performed on them by adding custom toolbar buttons.
	selectable: PropTypes.bool,
	// Flag that determines if delete single action will prompt a confirm dialog before triggering 'onDelete' event
	confirmDelete: PropTypes.bool,
	// Flag that determines if delete selected action will prompt a confirm dialog before triggering 'onDelete' event
	confirmDeleteSelected: PropTypes.bool,
	// Flag that determines if clear action will prompt a confirm dialog before triggering 'onDelete' event
	confirmClear: PropTypes.bool,
	// Flag that determines if labels will be rendered (@see fieldLabel, comparatorLabel, valueLabel).
	showLabels: PropTypes.bool,
	// Flag that determines if toolbar will be rendered
	showToolbar: PropTypes.bool,
	// Array of custom toolbar buttons
	// @note Custom tab button will be rendered at the end of the default toolbar. Buttons 'onClick' prop will get 
	// additional argument (apart from the click event) that will contain a list of all selected items and a list of all
	// items in that order.
	customToolbarButtons: PropTypes.arrayOf(PropTypes.shape({
		// Button element 'id' attribute.
		id: PropTypes.string,
		// Button element CSS class attribute.
		className: PropTypes.string,
		// The default behavior of the button. Possible values are: 'submit', 'reset' or 'button'.
		type: PropTypes.string,
		// Button display type ('none', 'solid', 'transparent', ...)
		displayType: PropTypes.oneOf(BUTTON_DISPLAY_TYPES),
		// Button display style ('default', 'success', 'error', ...)
		displayStyle: PropTypes.oneOf(BUTTON_STYLES),
		// If true, bigger button will be rendered.
		big: PropTypes.bool,
		// The name of the button, submitted as a pair with the button’s value as part of the form data.
		name: PropTypes.string,
		// Defines the value associated with the button’s name when it’s submitted with the form data. This value is passed
		// to the server in params when the form is submitted.
		value: PropTypes.string,
		// This Boolean attribute specifies that the button should have input focus when the page loads. 
		// @note Only one element in a document can have this attribute.
		autofocus: PropTypes.bool,
		// This Boolean attribute prevents the user from interacting with the button: it cannot be pressed or focused.
		disabled: PropTypes.bool,
		// If true, button will not be rendered.
		hide: PropTypes.bool,
		// Button label rendered as a child of the <button> component before any other child elements but after the icon.
		label: PropTypes.string,
		// Set to true to support HTML in 'label' prop.
		// @warning Be careful when using this flag because it can cause security issues. It uses 'dangerouslySetInnerHTML' 
		// to allow HTML content. 
		allowHtmlLabel: PropTypes.bool,
		// Font icon symbol name.
		icon: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
		// If true, icon will spin (if supported by font icon set used).
		spinIcon: PropTypes.bool,
		// Icon props
		// @see Icon component
		iconProps: PropTypes.object,

		// Events
		onClick: PropTypes.func,
	})),
	// Flag that determines if bottom toolbar will be rendered
	showBottomToolbar: PropTypes.bool,
	// Flag that specifies if list item actions (delete, add, ...) should be rendered
	showItemActions: PropTypes.bool,
	// Flag that specifies if the whole component will be disabled
	disabled: PropTypes.bool,

	// Property name of the condition id in data item object
	idSelector: PropTypes.string,
	// Property name of the condition ordinal in data item object
	ordinalSelector: PropTypes.string,
	// Property name of the logical operator in data item object
	logicalOperatorSelector: PropTypes.string,
	// Property name of the condition field in data item object
	fieldSelector: PropTypes.string,
	// Condition field label
	fieldLabel: PropTypes.string,
	// Display type used to render the field element 
	fieldDisplayType: PropTypes.oneOf(CONDITION_LIST_FIELD_DISPLAY_TYPES),
	// Condition field component props
	// @note This will depend on condition field display type ('fieldDisplayType' prop) but essentially use this to 
	// specify props for component used to render the condition field.
	fieldDisplayOptions: PropTypes.object,
	// Condition field custom component if display type is custom (CONDITION_LIST_FIELD_DISPLAY_TYPE_CUSTOM)
	fieldCustomComponent: PropTypes.elementType,
	// Flag that determines if field should use a simple value (not an object)
	// @note Component will always use a ConditionListFieldItemDataObject data object to store the field. Simple value is 
	// defined for loading and returning field data from a parent component.
	fieldSimpleValue: PropTypes.bool,
	// Property name of the condition field item label in field item object
	fieldItemLabelSelector: PropTypes.string,
	// Property name of the condition field item value in field item object
	fieldItemValueSelector: PropTypes.string,
	// Property name of the condition field item display type in field item object
	fieldItemDisplayTypeSelector: PropTypes.string,
	// Property name of the condition field item display options in field item object
	fieldItemDisplayOptionsSelector: PropTypes.string,
	// Property name of the condition field item supported comparators in field item object
	fieldItemSupportedComparatorsSelector: PropTypes.string,
	// Property name of the comparator in data item object
	comparatorSelector: PropTypes.string,
	// Comparator label
	comparatorLabel: PropTypes.string,
	// Map of the comparators based on field display type
	// @see CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE_COMPARATOR_MAP const for reference.
	comparatorDisplayTypeMap: PropTypes.object,
	// Property name of the condition value in data item object
	valueSelector: PropTypes.string,
	// Condition value label
	valueLabel: PropTypes.string,
	// Property name of the open parenthesis in data item object
	openParenthesisSelector: PropTypes.string,
	// Property name of the close parenthesis in data item object
	closeParenthesisSelector: PropTypes.string,
	// Flag that specifies if parenthesis will be enabled
	enableParenthesis: PropTypes.bool,
	// DOM selector for parent that will handle scrolling
	// @note This will be ignored if 'maxHeight' is set.
	scrollParentSelector: PropTypes.string,
	// Specifies when condition value will be cleared
	clearValueType: PropTypes.oneOf(CONDITION_LIST_VALUE_CLEAR_TYPES),
	// Object defining empty values for each field item display type
	// @note If not specified, default empty values wil be used (@see CONDITION_LIST_FIELD_ITEM_EMPTY_VALUES).
	// @type {Object<ConditionListFieldItemDisplayType, any>}
	emptyValues: PropTypes.object,
	
	// Main component data
	data: PropTypes.arrayOf(PropTypes.object),
	// Flag that specifies if data should be mapped when loading and returning it.
	// @important If false, data must be an array of ConditionListItemDataObject data objects!
	mapData: PropTypes.bool,
	// Errors to show for each individual row by GUIID
	errors: PropTypes.object,

	// Events -----------------------------------------------------------------------------------------------------------
	onChange: PropTypes.func, // Arguments: Array of conditions
	onValidationErrorsChange: PropTypes.func, // Arguments: Updated validation errors
};

/**
 * Define component default values for own props
 */
ConditionList.defaultProps = {
	styleName: 'default',
	itemStyleName: 'default',
	toolbarStyleName: 'standard',
	id: '',
	className: '',
	itemClassName: '',
	readOnly: false,
	selectable: true,
	confirmDelete: true,
	confirmDeleteSelected: true,
	confirmClear: true,
	showLabels: true,
	showToolbar: true,
	customToolbarButtons: [],
	showBottomToolbar: true,
	showItemActions: true,

	idSelector: 'id',
	ordinalSelector: 'ordinal',
	logicalOperatorSelector: 'logicalOperator',
	fieldSelector: 'field',
	fieldLabel: '',
	fieldDisplayType: CONDITION_LIST_FIELD_DISPLAY_TYPE.TEXT,
	fieldDisplayOptions: null,
	fieldCustomComponent: null,
	fieldSimpleValue: false,
	fieldItemLabelSelector: 'label',
	fieldItemValueSelector: 'value',
	fieldItemDisplayTypeSelector: 'displayType',
	fieldItemDisplayOptionsSelector: 'displayOptions',
	fieldItemSupportedComparatorsSelector: 'supportedComparators',
	comparatorSelector: 'comparator',
	comparatorLabel: '',
	comparatorDisplayTypeMap: CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE_COMPARATOR_MAP,
	valueSelector: 'value',
	valueLabel: '',
	openParenthesisSelector: 'openParenthesis',
	closeParenthesisSelector: 'closeParenthesis',
	enableParenthesis: false,
	clearValueType: CONDITION_LIST_VALUE_CLEAR_TYPE.ON_TYPE_CHANGE,
	emptyValues: CONDITION_LIST_FIELD_ITEM_EMPTY_VALUES,

	mapData: true,
};

export * from './const';
export default ConditionList;